import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { setLocale as setFormatLocale, subscribeLocale } from "@/lib/formatDate";
import { useAuth } from "@/context/AuthContext";

const LocaleContext = createContext(null);

const DEFAULTS = {
  timezone: "Europe/Rome",
  date_format: "dd/MM/yyyy",
  time_format: "24h",
  week_start: "monday",
};

export function LocaleProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [effective, setEffective] = useState(DEFAULTS);
  const [globalLocale, setGlobalLocale] = useState(DEFAULTS);
  const [userOverride, setUserOverride] = useState(null);
  const [isPrimaryOwner, setIsPrimaryOwner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, force] = useState(0);

  // riflette il valore effettivo nel modulo di formattazione, così fmtDate/fmtDateTime
  // lo prendono in modo trasparente ovunque nella piattaforma.
  useEffect(() => { setFormatLocale(effective); }, [effective]);
  useEffect(() => subscribeLocale(() => force((n) => n + 1)), []);

  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const { data } = await api.get("/platform/locale");
      setEffective(data.effective || DEFAULTS);
      setGlobalLocale(data.global || DEFAULTS);
      setUserOverride(data.user_override || null);
      setIsPrimaryOwner(!!data.is_primary_owner);
    } catch { /* ignore: usa defaults */ }
    setLoading(false);
  }, [isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  const updateGlobal = useCallback(async (payload) => {
    const { data } = await api.put("/platform/locale/global", payload);
    setEffective(data.effective);
    setGlobalLocale(data.global);
    setUserOverride(data.user_override || null);
    return data;
  }, []);

  const updateMe = useCallback(async (payload) => {
    const { data } = await api.put("/platform/locale/me", payload);
    setEffective(data.effective);
    setGlobalLocale(data.global);
    setUserOverride(data.user_override);
    return data;
  }, []);

  const resetMe = useCallback(async () => {
    const { data } = await api.delete("/platform/locale/me");
    setEffective(data.effective);
    setGlobalLocale(data.global);
    setUserOverride(null);
    return data;
  }, []);

  return (
    <LocaleContext.Provider value={{
      locale: effective, globalLocale, userOverride, isPrimaryOwner, loading,
      reload: load, updateGlobal, updateMe, resetMe,
    }}>
      {children}
    </LocaleContext.Provider>
  );
}

export const useLocale = () => useContext(LocaleContext);
